import { EnergyType, Quadrant, WasteType } from './EnergyFlow'

export type HourlyEnergy = {
  energyType: EnergyType
  value: number
}
export type PrimaryHourlyEnergyFlow = HourlyEnergy & {
  quadrant: Quadrant
  wasteType?: WasteType
  demandId: string
}
export type SecondaryHourlyEnergyFlow = Omit<
  PrimaryHourlyEnergyFlow,
  'demandId'
> & {
  providerId: string
}
export type HourlyEnergyFlow =
  | PrimaryHourlyEnergyFlow
  | SecondaryHourlyEnergyFlow

export const isPrimaryHourlyEnergyFlow = (
  flow: HourlyEnergyFlow,
): flow is PrimaryHourlyEnergyFlow =>
  Boolean((flow as PrimaryHourlyEnergyFlow).demandId)

export const isSecondaryHourlyEnergyFlow = (
  flow: HourlyEnergyFlow,
): flow is SecondaryHourlyEnergyFlow =>
  Boolean((flow as SecondaryHourlyEnergyFlow).providerId)

const createHourlyEnergyFlow =
  (quadrant: Quadrant, wasteType?: WasteType) =>
  (
    demandId: string,
    energyType: EnergyType,
    value: number,
  ): PrimaryHourlyEnergyFlow => ({
    demandId,
    quadrant,
    value,
    energyType,
    wasteType,
  })

export const createHourlyWaste = (
  demandId: string,
  energyType: EnergyType,
  value: number,
  wasteType = WasteType.Lost,
): PrimaryHourlyEnergyFlow =>
  createHourlyEnergyFlow(Quadrant.Waste, wasteType)(demandId, energyType, value)
export const createHourlyProvided = createHourlyEnergyFlow(Quadrant.Provided)
export const createHourlyFromEnvironment = createHourlyEnergyFlow(
  Quadrant.FromEnvironment,
)
export const createHourlyRequirement = createHourlyEnergyFlow(
  Quadrant.Requirement,
)

const createSecondaryHourlyEnergyFlow =
  (quadrant: Quadrant, wasteType?: WasteType) =>
  (
    providerId: string,
    energyType: EnergyType,
    value: number,
  ): SecondaryHourlyEnergyFlow => ({
    providerId,
    quadrant,
    value,
    energyType,
    wasteType,
  })

export const createSecondaryHourlyWaste = (
  providerId: string,
  energyType: EnergyType,
  value: number,
  wasteType = WasteType.Lost,
): SecondaryHourlyEnergyFlow =>
  createSecondaryHourlyEnergyFlow(Quadrant.Waste, wasteType)(
    providerId,
    energyType,
    value,
  )
export const createSecondaryHourlyProvided = createSecondaryHourlyEnergyFlow(
  Quadrant.Provided,
)
export const createSecondaryHourlyFromEnvironment =
  createSecondaryHourlyEnergyFlow(Quadrant.FromEnvironment)
export const createSecondaryHourlyRequirement = createSecondaryHourlyEnergyFlow(
  Quadrant.Requirement,
)

const createNewHourlyEnergyFlowFromExisting =
  (quadrant: Quadrant) =>
  (
    flow: HourlyEnergyFlow,
    energyType: EnergyType,
    value: number,
  ): HourlyEnergyFlow => {
    if (isPrimaryHourlyEnergyFlow(flow)) {
      return createHourlyEnergyFlow(quadrant)(flow.demandId, energyType, value)
    }

    if (isSecondaryHourlyEnergyFlow(flow)) {
      return createSecondaryHourlyEnergyFlow(quadrant)(
        flow.providerId,
        energyType,
        value,
      )
    }

    throw new Error('Error creating energy flow')
  }

export const createHourlyWasteFrom = createNewHourlyEnergyFlowFromExisting(
  Quadrant.Waste,
)
export const createHourlyProvidedFrom = createNewHourlyEnergyFlowFromExisting(
  Quadrant.Provided,
)
export const createHourlyFromEnvironmentFrom =
  createNewHourlyEnergyFlowFromExisting(Quadrant.FromEnvironment)
export const createHourlyRequirementFrom =
  createNewHourlyEnergyFlowFromExisting(Quadrant.Requirement)
